import React, { useState, useEffect, useRef } from 'react';
import { signOut } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { collection, doc, getDoc, serverTimestamp, setDoc, updateDoc, increment } from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
    Check, Loader2, Zap, Gift,
    Building2, Copy, ArrowLeft, AlertCircle, MessageCircle, Clock,
    Upload, FileText, Trash2, CheckCircle2, X
} from 'lucide-react';
import { auth, db, functions, storage } from '../firebase';
import { buildChaseAdminDeeplink } from '../utils/whatsappTemplate';
import { FLOW_PROMO, computeAmountSen, formatSenToRM } from '../config/promo';
import PricingSection from './PricingSection';
import SalesNav from './SalesNav';
import FaqSection from './FaqSection';

const claimFreeTrialFn = httpsCallable(functions, 'claimFreeTrial');

const StartGate = ({ user, licenseStatus, isRenewalEligible = false, onLicenseActivated, onClose, viewer = {} }) => {
    // BARU: isRenewalEligible dari App.jsx — multi-signal check (paidAt|licenseKey|tier='paid')
    // Independent of licenseStatus. Robust against admin padam licenses/{uid} doc.
    // Default false untuk backward-safe kalau parent terlupa hantar prop.
    // viewer = { loggedIn, tier, plan, licenseStatus, allowedModules } dari App.jsx —
    // dipass terus ke PricingSection (state matrix Pelan Semasa / Sambung / bundle FLOWNITY).
    // Default {} backward-safe → PricingSection render macam anon.

    const [view, setView] = useState('pricing'); // 'pricing' | 'transfer' | 'success'
    const [error, setError] = useState('');
    const [claimLoading, setClaimLoading] = useState(false);
    const [claimSuccess, setClaimSuccess] = useState(false);
    const [copiedField, setCopiedField] = useState(null);

    // ─── Payment proof submission state ───
    const [proofFile, setProofFile] = useState(null);
    const [proofPreview, setProofPreview] = useState(null); // dataURL untuk image preview; null untuk PDF
    const [submitError, setSubmitError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const fileInputRef = useRef(null);

    // ─── PROMO RAYA: Flow Ultra (Sharing) add-on ───
    const [flowPromo, setFlowPromo] = useState(null); // { slotsTotal, slotsUsed, active }
    const [addFlow] = useState(false);     // Promo Raya lama (dorman — add-on Flow dibuang dari model baru)
    const [payLoading, setPayLoading] = useState(false);

    useEffect(() => {
        let cancelled = false;
        getDoc(doc(db, 'promos', FLOW_PROMO.docId))
            .then(snap => { if (!cancelled && snap.exists()) setFlowPromo(snap.data()); })
            .catch(e => { console.warn('[StartGate] promo fetch failed (non-fatal):', e); });
        return () => { cancelled = true; };
    }, []);

    const flowSlotsTotal = flowPromo?.slotsTotal ?? FLOW_PROMO.defaultSlotsTotal;
    const flowSlotsUsed = flowPromo?.slotsUsed ?? 0;
    const flowSlotsLeft = Math.max(0, flowSlotsTotal - flowSlotsUsed);
    // Add-on hanya untuk user BARU (bukan renewal), promo aktif, & masih ada slot.
    const flowPromoAvailable = !!flowPromo && flowPromo.active !== false && flowSlotsLeft > 0 && !isRenewalEligible;
    const wantFlow = addFlow && flowPromoAvailable;

    const handleFileSelect = (file) => {
        setSubmitError('');
        if (!file) {
            setProofFile(null);
            setProofPreview(null);
            return;
        }
        const isImage = file.type.startsWith('image/');
        const isPdf = file.type === 'application/pdf';
        if (!isImage && !isPdf) {
            setSubmitError('Format tidak disokong. Sila upload gambar atau PDF.');
            return;
        }
        if (file.size > 10 * 1024 * 1024) {
            setSubmitError('Saiz fail terlalu besar. Maksimum 10MB.');
            return;
        }
        setProofFile(file);
        if (isImage) {
            const reader = new FileReader();
            reader.onload = (e) => setProofPreview(e.target?.result || null);
            reader.readAsDataURL(file);
        } else {
            setProofPreview(null);
        }
    };

    const handleRemoveFile = () => {
        setProofFile(null);
        setProofPreview(null);
        setSubmitError('');
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    // ─── Billplz payment (gateway auto) — ganti transfer manual di bahagian depan ───
    const handleBillplzPay = async (planKey = 'premium', opts = {}) => {
        setPayLoading(true);
        try {
            const createBill = httpsCallable(functions, 'createBillplzBill');
            // Map planKey → param createBillplzBill (bundle = plan premium + flag wantFlownityAddon)
            // wantFlowAddon dari checkbox Flow Ultra (Sharing) dalam PricingSection — user baru sahaja
            const payload = planKey === 'premium_renew_flownity'
                ? { returnOrigin: window.location.origin, wantFlowAddon: false, plan: 'premium', wantFlownityAddon: true }
                : {
                    returnOrigin: window.location.origin,
                    // Sama seperti PricingBaruView — dulu dipaku 'premium' sahaja,
                    // jadi add-on pada Fighter/SMART hilang sebelum sampai server.
                    wantFlowAddon: (planKey === 'premium' || planKey === 'fighter' || planKey === 'smart') && !!opts.wantFlowAddon,
                    plan: planKey,
                    ...(planKey === 'smart' && opts.smartModule ? { smartModule: opts.smartModule } : {}),
                };
            const res = await createBill(payload);
            const url = res?.data?.url;
            if (!url) throw new Error('URL bayaran tiada');
            window.location.href = url; // redirect ke halaman bayar Billplz
        } catch (err) {
            console.error('[Billplz] gagal mula bayaran:', err);
            setPayLoading(false);
            // Guard server (addon/bundle) hantar mesej BM formal — papar terus pada pengguna.
            const knownCode = err?.code === 'functions/failed-precondition' || err?.code === 'functions/already-exists';
            alert(knownCode && err?.message ? err.message : 'Maaf, ada masalah memulakan pembayaran. Sila cuba lagi atau hubungi admin.');
        }
    };

    const handleSubmitProof = async () => {
        if (!proofFile || !user) return;
        setSubmitError('');
        setIsSubmitting(true);
        try {
            const uid = user.uid;
            const email = user.email || '';
            // Pull whatsappNumber dari user doc (untuk admin nampak dalam dashboard).
            let whatsappNumber = '';
            try {
                const usnap = await getDoc(doc(db, 'users', uid));
                whatsappNumber = usnap.exists() ? (usnap.data()?.whatsappNumber || '') : '';
            } catch (lookupErr) {
                console.debug('[StartGate] whatsappNumber lookup failed (non-fatal):', lookupErr);
            }

            // BARU: amount + type ikut renewal mode
            const submissionType = isRenewalEligible ? 'renewal' : 'new';
            const baseSen = isRenewalEligible ? 9900 : 19900; // sen — RM99 renewal | RM199 new

            // PROMO RAYA: re-check slot di submit (slot ditolak masa submit, bukan approve).
            if (wantFlow) {
                try {
                    const freshSnap = await getDoc(doc(db, 'promos', FLOW_PROMO.docId));
                    const fresh = freshSnap.exists() ? freshSnap.data() : null;
                    const ok = fresh && fresh.active !== false && (fresh.slotsUsed ?? 0) < (fresh.slotsTotal ?? 0);
                    if (!ok) {
                        setSubmitError('Maaf, slot Flow Raya baru sahaja habis. Sila matikan pilihan Flow (transfer RM199 sahaja) atau WhatsApp admin.');
                        setIsSubmitting(false);
                        return;
                    }
                } catch (e) {
                    console.warn('[StartGate] slot recheck failed (proceed):', e);
                }
            }
            const flowSen = wantFlow ? FLOW_PROMO.priceSen : 0;
            const amount = baseSen + flowSen; // RM199 atau RM248 (sen)

            // Single-write flow: generate ID → upload Storage → create doc dengan URL siap
            const submissionRef = doc(collection(db, 'payment_submissions'));
            const ext = proofFile.name.split('.').pop() || (proofFile.type === 'application/pdf' ? 'pdf' : 'jpg');
            const path = `payment_proofs/${uid}/${submissionRef.id}.${ext}`;
            const fileRef = storageRef(storage, path);
            await uploadBytes(fileRef, proofFile, { contentType: proofFile.type });
            const url = await getDownloadURL(fileRef);
            await setDoc(submissionRef, {
                uid,
                email,
                whatsappNumber,
                amount,
                baseSen,                                    // BARU — base SESAAT (sen)
                flowAddon: flowSen > 0,                      // BARU — true kalau beli Flow add-on
                flowAddonSen: flowSen,                       // BARU — 4900 (RM49) atau 0
                type: submissionType,
                status: 'pending',
                submittedAt: serverTimestamp(),
                proofImageUrl: url,
                proofPath: path,
                proofContentType: proofFile.type,
                proofFileName: proofFile.name
            });
            // PROMO RAYA: naik counter slot (non-fatal — submission lebih penting).
            if (flowSen > 0) {
                try {
                    await updateDoc(doc(db, 'promos', FLOW_PROMO.docId), { slotsUsed: increment(1) });
                } catch (e) {
                    console.warn('[StartGate] slot increment failed (non-fatal):', e);
                }
            }
            setView('success');
            setIsSubmitting(false);
        } catch (err) {
            console.error('[StartGate] Submit proof failed:', err);
            setSubmitError('Gagal hantar submission: ' + (err?.message || 'unknown error'));
            setIsSubmitting(false);
        }
    };

    const handleClaimTrial = async () => {
        setClaimLoading(true);
        setError('');
        try {
            await claimFreeTrialFn({});
            setClaimSuccess(true);
            // Flag picked up by App.jsx post-license effect → redirect to /r/support
            try { sessionStorage.setItem('postTrialClaimRedirect', '1'); } catch (_) {}
            setTimeout(() => onLicenseActivated(), 1500);
        } catch (err) {
            if (err.code === 'functions/already-exists') {
                setError('Percubaan percuma sudah dituntut sebelum ini. Sila langgan untuk teruskan.');
            } else if (err.code === 'functions/failed-precondition') {
                setError(err.message || 'Tidak boleh tuntut percubaan.');
            } else {
                setError('Gagal menuntut percubaan percuma. Sila cuba lagi.');
            }
            setClaimLoading(false);
        }
    };

    const handleCopyAccount = () => {
        navigator.clipboard?.writeText('551342121828');
        setCopiedField('account');
        setTimeout(() => setCopiedField(null), 2000);
    };

    const handleLogout = async () => {
        await signOut(auth);
    };

    /* ⭐ PANGKAH (arahan user 2026-08-26) — semua jenis pelawat gate boleh tutup
       skrin ini dan masuk ke aplikasi. Ia BUKAN membuka ciri berbayar: modul
       dikawal oleh `allowedModules` pada lesen, dan server tetap menolak setiap
       generate tanpa kuota. Yang dibuka hanyalah pintu untuk melihat-lihat dan
       menggunakan kerja yang sudah siap (cth withdraw komisen affiliate, akses Flow).
       ⚠️ Pilihan ini diingat untuk SESI ini sahaja — buka browser semula, gate
       muncul balik. Sengaja: gate ini juga skrin renew, jadi ia tidak boleh
       hilang selamanya selepas satu klik.
       ⚠️ 2026-09-30: dahulu butang ini `fixed top-3 right-3` — TERSOROK di belakang
       SalesNav (sticky, z-50 yang sama, dirender kemudian) jadi pelanggan tidak nampak
       / tidak boleh tekan. Kini duduk dalam kandungan: dalam kotak ungu (renewal)
       atau baris sendiri di atas tajuk trial (arahan user: kedudukan di kotak ungu). */
    const butangTutup = onClose ? (
        <button
            type="button"
            onClick={onClose}
            aria-label="Tutup"
            title="Tutup"
            className="w-8 h-8 rounded-full border border-[#e6e3db] bg-white text-[#6b6a64] hover:bg-[#faf9f5] hover:text-[#14130f] flex items-center justify-center shadow-sm transition-colors touch-manipulation"
        >
            <X className="w-4 h-4" />
        </button>
    ) : null;

    // WhatsApp chase admin deeplink (dipakai dalam success view).
    const chaseAdminUrl = buildChaseAdminDeeplink({
        email: user?.email || '',
        amountSen: computeAmountSen(isRenewalEligible ? 9900 : 19900, wantFlow),
        flowAddon: wantFlow,
    });

    return (
        <div
            className="min-h-screen min-h-[100dvh] flex flex-col bg-[#f4f3ef] text-[#14130f]"
            style={{
                fontFamily: "'Inter', sans-serif",
                paddingTop: 'env(safe-area-inset-top)',
                paddingBottom: 'env(safe-area-inset-bottom)',
            }}
        >
            {/* Header — nav kongsi (sama /sesaat-1 + /pricing-baru). User login di gate → papar Log Keluar. */}
            <SalesNav onLogout={handleLogout} />

            {/* Main content */}
            <main className="relative z-10 flex-1 flex items-start sm:items-center justify-center px-4 py-3 sm:py-8">
                <div className={`w-full ${view === 'pricing' ? 'max-w-5xl' : 'max-w-md'}`}>

                    {/* Pangkah — baris sendiri bila tiada kotak ungu (trial / view lain) */}
                    {butangTutup && !(view === 'pricing' && isRenewalEligible) && (
                        <div className="flex justify-end mb-2">{butangTutup}</div>
                    )}

                    {view === 'success' ? (
                        /* ═══ SUCCESS VIEW (selepas hantar submission) ═══ */
                        <div className="animate-[fadeIn_0.25s_ease-out]">
                            <div className="rounded-3xl border-2 border-emerald-300 bg-white p-5 sm:p-8 shadow-xl shadow-emerald-500/10">
                                <div className="flex justify-center mb-4">
                                    <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-emerald-500 to-green-500 flex items-center justify-center shadow-lg shadow-emerald-500/40">
                                        <CheckCircle2 className="w-9 h-9 sm:w-11 sm:h-11 text-white" strokeWidth={2.5} />
                                    </div>
                                </div>
                                <h2 className="text-center text-xl sm:text-2xl font-extrabold text-slate-800 mb-2">
                                    Berjaya Hantar! 🎉
                                </h2>
                                <p className="text-center text-sm text-slate-500 mb-6 leading-relaxed">
                                    Admin akan aktifkan akaun anda dalam masa terdekat. Anda akan terima maklumat di WhatsApp.
                                </p>
                                <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 mb-5">
                                    <div className="flex items-start gap-2.5">
                                        <Clock className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                                        <div className="text-xs text-amber-900 leading-relaxed">
                                            <strong className="font-bold">Masa proses:</strong> 1-24 jam selepas pengesahan admin. Pastikan WhatsApp anda aktif.
                                        </div>
                                    </div>
                                </div>
                                <a
                                    href={chaseAdminUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-full py-3.5 rounded-xl font-extrabold text-sm transition-all flex items-center justify-center gap-2 cursor-pointer bg-gradient-to-r from-emerald-500 to-green-500 text-white shadow-lg shadow-emerald-500/40 hover:shadow-xl hover:shadow-emerald-500/50 hover:opacity-95 active:scale-[0.98] touch-manipulation"
                                >
                                    <MessageCircle className="w-4 h-4 fill-current" />
                                    <span>Wasap Admin Untuk Cepatkan Proses</span>
                                </a>
                                <p className="text-[11px] text-slate-400 mt-3 text-center leading-relaxed">
                                    Optional — admin akan tetap proses submission anda walaupun tanpa WhatsApp.
                                </p>
                            </div>
                        </div>
                    ) : view === 'pricing' ? (
                        /* ═══ PRICING — 2 kad + table compare (komponen dikongsi) ═══ */
                        <div className="relative animate-[fadeIn_0.25s_ease-out]">
                            {isRenewalEligible && (
                                <div className={`relative mb-4 ${butangTutup ? 'px-11' : 'px-4'} py-3 rounded-2xl bg-purple-50 border border-purple-200 text-center`}>
                                    {butangTutup && (
                                        <div className="absolute top-2 right-2">{butangTutup}</div>
                                    )}
                                    <p className="text-[12px] font-bold text-purple-700 flex items-center justify-center gap-1.5">
                                        🔄 PELANGGAN SEDIA ADA · LANGGANAN TAMAT
                                    </p>
                                    <p className="text-[11px] text-purple-600 mt-0.5">Renew untuk sambung akses — harga renew dikira automatik di halaman bayaran.</p>
                                </div>
                            )}

                            {error && (
                                <div className="mb-4 px-3 py-2.5 rounded-lg bg-red-50 border border-red-200 text-red-700 text-xs font-medium flex items-start gap-2">
                                    <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                                    {error}
                                </div>
                            )}

                            {/* Free Trial di ATAS — biar user nampak pilihan percuma dulu (hide untuk renewal) */}
                            {!isRenewalEligible && (
                                <div className="max-w-md mx-auto mb-8">
                                    {/* Instrument Serif (aksen italik) — sama gaya hero salespage */}
                                    <style>{`@import url('https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&display=swap');`}</style>
                                    <h2 className="text-center text-slate-900 mb-4" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 'clamp(24px, 6vw, 34px)', lineHeight: 1.08, letterSpacing: '-0.02em' }}>
                                        Nak <span style={{ fontFamily: "'Instrument Serif', serif", fontStyle: 'italic', fontWeight: 400 }}>cuba dulu</span> secara <span style={{ fontFamily: "'Instrument Serif', serif", fontStyle: 'italic', fontWeight: 400 }}>percuma?</span>
                                    </h2>
                                    {claimSuccess ? (
                                        <div className="w-full flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 font-bold text-sm">
                                            <Check className="w-4 h-4" strokeWidth={3} />
                                            5 Coins dituntut! Memuatkan...
                                        </div>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={handleClaimTrial}
                                            disabled={claimLoading}
                                            className="w-full flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl bg-emerald-50 hover:bg-emerald-100 active:bg-emerald-100 border border-emerald-200 hover:border-emerald-300 text-emerald-700 font-bold text-sm active:scale-[0.98] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                                        >
                                            {claimLoading ? (
                                                <><Loader2 className="w-4 h-4 animate-spin" /> Sedang menuntut...</>
                                            ) : (
                                                <><Zap className="w-4 h-4 fill-current" /> Claim 5 Coins Percuma</>
                                            )}
                                        </button>
                                    )}
                                    <div className="flex items-center gap-3 mt-6">
                                        <div className="flex-1 h-px bg-slate-200" />
                                        <span className="text-[10px] font-semibold text-slate-400 tracking-widest">ATAU AMBIL PAKEJ BERBAYAR</span>
                                        <div className="flex-1 h-px bg-slate-200" />
                                    </div>
                                </div>
                            )}

                            {/* Kad pakej + table compare — CTA trigger Billplz (premium/fighter/addon/bundle) */}
                            <PricingSection onSubscribe={handleBillplzPay} payLoading={payLoading} fadeColor="#f4f3ef" viewer={viewer} />

                            {/* FAQ — komponen dikongsi (auto-sync /pricing-baru + /sesaat-1) */}
                            <FaqSection />
                        </div>
                    ) : (
                        /* ═══ TRANSFER MANUAL VIEW ═══ */
                        <div className="animate-[fadeIn_0.25s_ease-out]">
                            <button
                                type="button"
                                onClick={() => setView('pricing')}
                                className="mb-3 sm:mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-slate-800 active:text-slate-900 transition px-3 py-2 -ml-3 rounded-lg hover:bg-white/60 active:bg-white/80 touch-manipulation"
                            >
                                <ArrowLeft className="w-4 h-4" />
                                Kembali ke pelan
                            </button>

                            <div className={`rounded-3xl border-2 ${isRenewalEligible ? 'border-purple-300 shadow-purple-500/10' : 'border-amber-300 shadow-amber-500/10'} bg-white p-5 sm:p-8 shadow-xl`}>
                                {/* Icon + title */}
                                <div className="text-center mb-5 sm:mb-6">
                                    <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-br ${isRenewalEligible ? 'from-purple-500 to-fuchsia-500 shadow-purple-500/30' : 'from-amber-500 to-orange-500 shadow-amber-500/30'} flex items-center justify-center mx-auto mb-3 sm:mb-4 shadow-lg`}>
                                        <Building2 className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                                    </div>
                                    {isRenewalEligible && (
                                        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-purple-100 text-purple-700 text-[10px] font-bold mb-2">
                                            🔄 RENEWAL PELANGGAN SEDIA ADA
                                        </span>
                                    )}
                                    <h3 className="text-lg sm:text-xl font-extrabold text-slate-800">
                                        {isRenewalEligible ? 'Renew Langganan' : 'Arahan Pembayaran Manual'}
                                    </h3>
                                    <p className="text-[11px] sm:text-xs text-slate-500 mt-1.5 sm:mt-2 max-w-xs mx-auto leading-relaxed">
                                        {isRenewalEligible
                                            ? 'Transfer RM99 untuk renew langganan. Modul akses + setting sedia ada akan dikekalkan.'
                                            : 'Transfer ke akaun bank di bawah, kemudian hantar bukti pembayaran kepada kami via WhatsApp.'}
                                    </p>
                                </div>

                                {/* Amount */}
                                <div className={`rounded-xl bg-gradient-to-r ${isRenewalEligible ? 'from-purple-50 to-fuchsia-50 border border-purple-200' : 'from-amber-50 to-orange-50 border border-amber-200'} p-3.5 sm:p-4 mb-4 sm:mb-5 text-center`}>
                                    <p className={`text-[10px] sm:text-[11px] font-bold ${isRenewalEligible ? 'text-purple-700' : 'text-amber-700'} uppercase tracking-wider`}>Jumlah untuk transfer</p>
                                    <p className="text-2xl sm:text-3xl font-extrabold text-slate-800 mt-1">
                                        {isRenewalEligible ? 'RM99.00' : formatSenToRM(computeAmountSen(19900, wantFlow))}
                                    </p>
                                    <p className="text-[10px] sm:text-[11px] text-slate-500 mt-1">
                                        {isRenewalEligible
                                            ? 'Renewal • +30 hari'
                                            : wantFlow
                                                ? 'Bulan pertama • SESAAT + Flow Ultra (Sharing)'
                                                : 'Bulan pertama • Pakej Premium SESAAT'}
                                    </p>
                                </div>

                                {/* Bank details */}
                                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3.5 sm:p-4 mb-4 sm:mb-5 divide-y divide-slate-200">
                                    <div className="pb-3">
                                        <p className="text-[11px] sm:text-xs text-slate-500 mb-0.5">Bank</p>
                                        <p className="text-sm sm:text-base font-semibold text-slate-800">Maybank</p>
                                    </div>
                                    <div className="py-3">
                                        <p className="text-[11px] sm:text-xs text-slate-500 mb-0.5">Nama Pemegang Akaun</p>
                                        <p className="text-sm sm:text-base font-semibold text-slate-800">Monster Food Sdn Bhd</p>
                                    </div>
                                    <div className="pt-3 flex items-center justify-between gap-2">
                                        <div className="min-w-0">
                                            <p className="text-[11px] sm:text-xs text-slate-500 mb-0.5">Nombor Akaun</p>
                                            <p className="text-base sm:text-lg font-semibold text-slate-800 tracking-wide">5513 4212 1828</p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={handleCopyAccount}
                                            className="shrink-0 inline-flex items-center gap-1 px-2.5 sm:px-3 py-2 sm:py-1.5 rounded-lg bg-white border border-slate-200 text-[11px] sm:text-xs font-semibold text-slate-700 hover:bg-slate-100 active:bg-slate-200 active:scale-95 transition touch-manipulation"
                                        >
                                            {copiedField === 'account' ? (
                                                <><Check className="w-3 h-3" /> Disalin</>
                                            ) : (
                                                <><Copy className="w-3 h-3" /> Salin</>
                                            )}
                                        </button>
                                    </div>
                                </div>

                                {/* PROMO RAYA — ringkasan pilihan (read-only; pilih add-on dibuat di page pelan) */}
                                {!isRenewalEligible && wantFlow ? (
                                    <div className="rounded-xl border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-orange-50 p-3.5 sm:p-4 mb-4">
                                        <div className="flex items-center gap-2.5">
                                            <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-gradient-to-br from-amber-500 to-orange-500 text-white shrink-0">
                                                <Gift className="w-[15px] h-[15px]" />
                                            </span>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-[12px] font-bold text-slate-700">
                                                    Termasuk <span className="text-amber-700">Flow Ultra (Sharing)</span> · +RM49
                                                </p>
                                                <p className="text-[11px] text-slate-500 mt-0.5 leading-relaxed">
                                                    Akses akaun Flow akan diberi oleh admin selepas bayaran disahkan (via WhatsApp).
                                                </p>
                                            </div>
                                            <Check className="w-4 h-4 text-emerald-600 shrink-0" strokeWidth={3} />
                                        </div>
                                    </div>
                                ) : (
                                    <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 sm:p-4 mb-4">
                                        <div className="flex items-start gap-2 sm:gap-2.5">
                                            <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                                            <div className="text-[11px] sm:text-xs text-amber-900 leading-relaxed">
                                                {!isRenewalEligible && flowPromo && flowSlotsLeft <= 0 ? (
                                                    <>Slot promo Flow Raya dah <strong>habis</strong>. Anda boleh langgan SESAAT &amp; guna akaun Flow sendiri, atau WhatsApp admin untuk pilihan lain.</>
                                                ) : (
                                                    <>Untuk jana gambar &amp; video AI, anda perlukan akaun <strong>Flow (Google Labs) Ultra</strong>. Belum ada? WhatsApp admin — boleh pilih akaun <em>share</em> atau <em>private</em>.</>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* ─── Upload Bukti Pembayaran ─── */}
                                <div className="mb-4">
                                    <label className="block text-xs font-bold text-slate-700 mb-2">
                                        Upload Bukti Pembayaran <span className="text-rose-500">*</span>
                                    </label>
                                    {!proofFile ? (
                                        <label
                                            htmlFor="sg-proof-upload-input"
                                            className="flex flex-col items-center justify-center w-full p-5 sm:p-6 rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 hover:border-emerald-400 hover:bg-emerald-50/50 cursor-pointer transition touch-manipulation"
                                        >
                                            <Upload className="w-7 h-7 text-slate-400 mb-2" />
                                            <p className="text-xs font-semibold text-slate-600">Klik untuk pilih fail</p>
                                            <p className="text-[10px] text-slate-400 mt-1 text-center">Gambar atau PDF (max 10MB)</p>
                                            <input
                                                id="sg-proof-upload-input"
                                                ref={fileInputRef}
                                                type="file"
                                                accept="image/*,application/pdf,.heic,.heif"
                                                onChange={(e) => handleFileSelect(e.target.files?.[0] || null)}
                                                className="hidden"
                                            />
                                        </label>
                                    ) : (
                                        <div className="rounded-xl border border-emerald-300 bg-emerald-50 p-3 flex items-center gap-3">
                                            {proofPreview ? (
                                                <img src={proofPreview} alt="Preview" className="w-14 h-14 object-cover rounded-lg border border-emerald-200" />
                                            ) : (
                                                <div className="w-14 h-14 rounded-lg bg-rose-100 flex items-center justify-center">
                                                    <FileText className="w-7 h-7 text-rose-600" />
                                                </div>
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <p className="text-xs font-semibold text-slate-800 truncate">{proofFile.name}</p>
                                                <p className="text-[10px] text-slate-500 mt-0.5">{(proofFile.size / 1024).toFixed(0)} KB</p>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={handleRemoveFile}
                                                disabled={isSubmitting}
                                                className="p-2 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition disabled:opacity-50 touch-manipulation"
                                                title="Buang fail"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    )}
                                    {submitError && (
                                        <div className="mt-2 flex items-start gap-1.5 text-xs text-rose-600">
                                            <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                                            <span>{submitError}</span>
                                        </div>
                                    )}
                                </div>

                                <p className="text-[10px] sm:text-[11px] text-slate-500 mb-3 text-center flex items-center gap-1.5 justify-center">
                                    <Clock className="w-3 h-3" />
                                    Akaun anda akan diaktifkan dalam 1-24 jam selepas pengesahan
                                </p>

                                {/* Hantar Submission CTA */}
                                <button
                                    type="button"
                                    onClick={handleSubmitProof}
                                    disabled={!proofFile || isSubmitting}
                                    className="w-full py-3.5 rounded-xl font-extrabold text-sm transition-all flex items-center justify-center gap-2 cursor-pointer bg-gradient-to-r from-emerald-500 to-green-500 text-white shadow-lg shadow-emerald-500/40 hover:shadow-xl hover:shadow-emerald-500/50 hover:opacity-95 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none touch-manipulation"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            <span>Sedang hantar...</span>
                                        </>
                                    ) : (
                                        <>
                                            <Upload className="w-4 h-4" />
                                            <span>Hantar Submission Upgrade Premium</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    )}

                </div>
            </main>
        </div>
    );
};

export default StartGate;
